import { useEffect, useRef } from 'react';

import { css } from '@compiled/react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { companyTimelineMessages as messages } from './company-timeline-messages';

const HEADING_ID = 'company-timeline-heading';

interface TimelineEntry {
	/** Rendered verbatim and used as the `<time datetime>` value, so it must stay a plain year. */
	year: string;
	highlight?: MessageDescriptor;
	body: MessageDescriptor[];
	/** Rendered as a real list rather than line-broken copy. */
	list?: MessageDescriptor[];
}

const TIMELINE_ENTRIES: TimelineEntry[] = [
	{ year: '1998', body: [messages.entry1998] },
	{ year: '2001', body: [messages.entry2001] },
	{ year: '2002', body: [messages.entry2002] },
	{ year: '2003', body: [messages.entry2003] },
	{ year: '2005', highlight: messages.entry2005Highlight, body: [messages.entry2005] },
	{ year: '2006', body: [messages.entry2006] },
	{
		year: '2007',
		body: [messages.entry2007],
		list: [
			messages.entry2007Value1,
			messages.entry2007Value2,
			messages.entry2007Value3,
			messages.entry2007Value4,
			messages.entry2007Value5,
		],
	},
	{ year: '2009', body: [messages.entry2009] },
	{ year: '2011', body: [messages.entry2011] },
	{
		year: '2012',
		highlight: messages.entry2012Highlight,
		body: [messages.entry2012Anniversary, messages.entry2012Revenue],
	},
	{ year: '2013', body: [messages.entry2013Offices, messages.entry2013ServiceDesk] },
	{ year: '2015', body: [messages.entry2015] },
	{ year: '2017', body: [messages.entry2017] },
	{ year: '2018', body: [messages.entry2018Opsgenie, messages.entry2018Bengaluru] },
	{
		year: '2020',
		body: [messages.entry2020ServiceManagement, messages.entry2020TeamAnywhere],
	},
	{ year: '2021', body: [messages.entry2021] },
	{ year: '2022', highlight: messages.entry2022Highlight, body: [messages.entry2022] },
	{ year: '2023', body: [messages.entry2023Ai, messages.entry2023Loom] },
	{ year: '2024', body: [messages.entry2024Rovo, messages.entry2024Jira] },
	{ year: '2025', body: [messages.entry2025] },
];

const UniformCompanyTimeline = () => {
	const intl = useIntl();
	const scrollerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) {
			return;
		}

		// Keyboard users must be able to focus the track to scroll it (WCAG 2.1.1), but only when
		// there is something to scroll — otherwise it is a dead tab stop. Set from the DOM because
		// it depends on measured layout, which is not known at render time. The 1px allowance
		// absorbs sub-pixel layout rounding.
		const measure = () => {
			scroller.tabIndex = scroller.scrollWidth - scroller.clientWidth > 1 ? 0 : -1;
		};
		measure();

		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(measure);
		observer.observe(scroller);
		Array.from(scroller.children).forEach((child) => observer.observe(child));
		return () => observer.disconnect();
	}, []);

	const lastIndex = TIMELINE_ENTRIES.length - 1;

	return (
		<section css={sectionStyles} aria-labelledby={HEADING_ID}>
			<div
				ref={scrollerRef}
				css={scrollerStyles}
				role="group"
				aria-label={intl.formatMessage(messages.timelineLabel)}
			>
				<div css={introStyles}>
					<h2 id={HEADING_ID} css={headingStyles}>
						{intl.formatMessage(messages.heading)}
					</h2>
					<p css={paragraphStyles}>{intl.formatMessage(messages.introFounding)}</p>
					<p css={paragraphStyles}>{intl.formatMessage(messages.introToday)}</p>
					<div css={introRuleStyles} />
				</div>
				{/* `role="list"` restores list semantics that Safari/VoiceOver drop with `list-style: none`. */}
				<ol css={trackStyles} role="list">
					{TIMELINE_ENTRIES.map((entry, index) => (
						<li
							key={entry.year}
							css={[
								entryStyles,
								(index === 0 || index === lastIndex) && entryDotStyles,
								index === lastIndex && entryLastStyles,
							]}
						>
							<h3 css={yearStyles}>
								<time dateTime={entry.year}>{entry.year}</time>
							</h3>
							<div css={entryBodyStyles}>
								{entry.highlight && (
									<p className="company-timeline-highlight" css={highlightStyles}>
										{intl.formatMessage(entry.highlight)}
									</p>
								)}
								{entry.body.map((message) => (
									<p key={message.id} css={paragraphStyles}>
										{intl.formatMessage(message)}
									</p>
								))}
								{entry.list && (
									<ul css={valuesListStyles} role="list">
										{entry.list.map((message) => (
											<li key={message.id}>{intl.formatMessage(message)}</li>
										))}
									</ul>
								)}
							</div>
						</li>
					))}
				</ol>
			</div>
		</section>
	);
};

const TIMELINE_RULE_HEIGHT = '0.625rem';
const TIMELINE_DOT_SIZE = '1.5625rem';
// Centres the dot on the rule: (dot - rule) / 2.
const TIMELINE_DOT_OFFSET = '-0.46875rem';

const sectionStyles = css({
	backgroundColor: wacToken('color.neutral.light200'),
});

const scrollerStyles = css({
	display: 'flex',
	flexWrap: 'nowrap',
	alignItems: 'flex-end',
	width: '100%',
	maxWidth: '100%',
	// Without this the section is sized by its overflowing content, which would scroll the whole
	// page sideways instead of just this track.
	minWidth: 0,
	paddingBlock: token('space.600'),
	paddingInline: token('space.300'),
	overflowX: 'auto',
	// `visible` on one axis computes to `auto` when the other scrolls, which would add an unwanted
	// vertical scrollbar.
	overflowY: 'hidden',
	// Stops the swipe chaining to the page and triggering back-navigation in Safari/Chrome.
	overscrollBehaviorX: 'contain',
	// Momentum scrolling on older iOS Safari.
	WebkitOverflowScrolling: 'touch',
	// Firefox and Chromium 121+; the `::-webkit-scrollbar` rules cover older WebKit/Blink.
	scrollbarWidth: 'thin',
	scrollbarColor: `${wacToken('color.neutral.light400')} transparent`,
	'&::-webkit-scrollbar': {
		height: token('space.100'),
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: wacToken('color.neutral.light400'),
		borderRadius: token('radius.full', '9999px'),
	},
	'@media (prefers-reduced-motion: no-preference)': {
		scrollBehavior: 'smooth',
	},
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: token('space.025'),
	},
	[media.above.md]: {
		paddingInline: token('space.500'),
	},
});

// Vertical rhythm comes from `gap` rather than child margins, so no trailing-margin reset is
// needed to keep the final element flush.
const introStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.300'),
	flex: '0 0 auto',
	width: 'min(85vw, 22.5rem)',
	backgroundColor: wacToken('color.neutral.light0'),
	padding: token('space.500'),
	marginInlineEnd: token('space.400'),
	[media.above.md]: {
		width: '28.75rem',
	},
});

const headingStyles = css({
	margin: 0,
	fontSize: '2rem',
	lineHeight: 1.16,
	fontWeight: 500,
	color: wacToken('color.primary.black'),
	[media.above.md]: {
		fontSize: '3rem',
	},
});

const paragraphStyles = css({
	margin: 0,
	fontSize: '1rem',
	lineHeight: 1.5,
	color: wacToken('color.primary.black'),
});

const entryBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
});

const introRuleStyles = css({
	width: '8.75rem',
	height: token('space.050'),
	backgroundColor: wacToken('color.primary.saffron'),
});

const trackStyles = css({
	display: 'flex',
	flexWrap: 'nowrap',
	alignItems: 'flex-end',
	flex: '0 0 auto',
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

// Gaps between entries come from padding rather than `gap` so the bottom rule stays unbroken.
const entryStyles = css({
	position: 'relative',
	display: 'flex',
	// Reverses the two children so the year sits below its copy while staying first in the reading
	// order, and anchors the content to the bottom of the tallest entry.
	flexDirection: 'column-reverse',
	justifyContent: 'flex-start',
	flex: '0 0 auto',
	width: 'min(80vw, 17.5rem)',
	paddingBlock: `${token('space.200')} ${token('space.400')}`,
	paddingInlineEnd: token('space.400'),
	'&::after': {
		content: '""',
		position: 'absolute',
		insetInline: 0,
		insetBlockEnd: 0,
		height: TIMELINE_RULE_HEIGHT,
		backgroundColor: wacToken('color.primary.blue'),
	},
	[media.above.md]: {
		width: '20rem',
	},
});

const entryDotStyles = css({
	'&::before': {
		content: '""',
		position: 'absolute',
		insetInlineStart: 0,
		insetBlockEnd: TIMELINE_DOT_OFFSET,
		width: TIMELINE_DOT_SIZE,
		height: TIMELINE_DOT_SIZE,
		borderRadius: '50%',
		backgroundColor: wacToken('color.primary.blue'),
	},
});

// The rule spans first dot to last dot, so the final entry carries a dot but no rule.
const entryLastStyles = css({
	'&::after': {
		display: 'none',
	},
});

const yearStyles = css({
	margin: 0,
	marginBlockStart: token('space.200'),
	fontSize: '1.25rem',
	lineHeight: 1.4,
	fontWeight: 400,
	color: wacToken('color.neutral.light800'),
});

// Declared against the class name as well as the element so the rule is (0,2,0) — a global
// stylesheet `p` rule out-specifies Compiled's single-class output and was winning.
const highlightStyles = css({
	'&.company-timeline-highlight': {
		margin: 0,
		fontSize: '1.75rem',
		lineHeight: 1.33,
		fontWeight: 500,
		color: wacToken('color.primary.black'),
	},
	[media.above.md]: {
		'&.company-timeline-highlight': {
			fontSize: '2.25rem',
		},
	},
});

const valuesListStyles = css({
	listStyle: 'none',
	margin: 0,
	padding: 0,
	fontSize: '1rem',
	lineHeight: 1.5,
	color: wacToken('color.primary.black'),
});

export default UniformCompanyTimeline;
